import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { X, Plus, ChevronUp, ChevronDown, Trash2, Copy, Eye, ExternalLink, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const KINDS = ["yesno", "yesno_comment", "comment_only"];

export default function ConsentFormManager() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // template id

  const load = React.useCallback(() => api.get("/admin/consent-templates").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => rows.filter((r) => (r.name + " " + r.key).toLowerCase().includes(query.toLowerCase())),
    [rows, query]
  );

  const toggleActive = async (r) => {
    await api.patch(`/admin/consent-templates/${r.id}/status?active=${!r.active}`);
    toast.success(!r.active ? "Activated" : "Deactivated");
    load();
  };
  const duplicate = async (r) => {
    await api.post(`/admin/consent-templates/${r.id}/duplicate`);
    toast.success("Duplicated as new draft");
    load();
  };

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div>
          <p className="mono-label">Consent Forms Manager</p>
          <h2 className="font-serif text-3xl font-light mt-1">All Consent Forms</h2>
        </div>
        <input
          data-testid="cf-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className="border hairline px-3 py-2 text-sm w-64"
        />
      </div>
      <div className="mt-6 border hairline bg-white">
        <table className="w-full text-sm">
          <thead className="bg-alt mono-label">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Key</th>
              <th className="text-right p-3">Version</th>
              <th className="text-right p-3">Questions</th>
              <th className="text-center p-3">Status</th>
              <th className="text-left p-3">Last Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className={`border-t hairline ${!r.active ? "bg-stone-50/50" : ""}`} data-testid={`cf-row-${r.key}`}>
                <td className={`p-3 ${!r.active ? "opacity-50" : ""}`}>
                  <div>{r.name}</div>
                  {r.linked_treatment_id && r.treatment && (
                    <div className="text-[10px] mono-label text-stone-500 mt-1">↳ {r.treatment.name}</div>
                  )}
                </td>
                <td className={`p-3 ${!r.active ? "opacity-50" : ""} text-stone-500 mono-label`}>{r.key}</td>
                <td className="p-3 text-right">v{r.current_version || 0}</td>
                <td className="p-3 text-right">{r.questions_count}</td>
                <td className="p-3 text-center">
                  {r.status === "published" && r.active ? (
                    <span className="mono-label inline-block px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]">PUBLISHED</span>
                  ) : r.status === "draft" ? (
                    <span className="mono-label inline-block px-2 py-1 border border-amber-400 bg-amber-100 text-amber-900 text-[10px]">DRAFT</span>
                  ) : (
                    <span className="mono-label inline-block px-2 py-1 border border-stone-300 bg-stone-100 text-stone-600 text-[10px]">INACTIVE</span>
                  )}
                </td>
                <td className="p-3 text-xs text-stone-500">{(r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 whitespace-nowrap text-right space-x-2">
                  <button data-testid={`cf-edit-${r.key}`} onClick={() => setEditing(r.id)} className="mono-label hover:text-stone-900">Edit</button>
                  <button data-testid={`cf-duplicate-${r.key}`} onClick={() => duplicate(r)} className="mono-label hover:text-stone-900" title="Duplicate">Duplicate</button>
                  <button data-testid={`cf-toggle-${r.key}`} onClick={() => toggleActive(r)} className="mono-label hover:text-stone-900">{r.active ? "Deactivate" : "Activate"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <TemplateEditor tid={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateEditor({ tid, onClose }) {
  const [t, setT] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [publishNote, setPublishNote] = useState("");
  const [previewing, setPreviewing] = useState(false);

  const load = React.useCallback(() => api.get(`/admin/consent-templates/${tid}`).then((r) => { setT(r.data); setDirty(false); }), [tid]);
  useEffect(() => { load(); }, [load]);

  if (!t) return null;
  const draft = t.draft || { title: "", intro: "", questions: [] };
  const patchDraft = (patch) => { setT({ ...t, draft: { ...draft, ...patch } }); setDirty(true); };
  const saveDraft = async () => {
    await api.patch(`/admin/consent-templates/${tid}`, {
      title: draft.title, intro: draft.intro, questions: draft.questions, name: t.name,
    });
    toast.success("Draft saved");
    setDirty(false); load();
  };
  const publish = async () => {
    if (dirty) await saveDraft();
    try {
      await api.post(`/admin/consent-templates/${tid}/publish`, { change_note: publishNote });
      toast.success("Published — now live on the client booking page");
      setPublishNote(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };
  const restore = async (v) => {
    if (!window.confirm(`Restore v${v} as a new draft? (History is preserved.)`)) return;
    await api.post(`/admin/consent-templates/${tid}/restore/${v}`);
    toast.success(`Restored v${v} → new draft`);
    load();
  };

  const qs = draft.questions || [];
  const updateQ = (i, patch) => {
    const next = qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q));
    patchDraft({ questions: next });
  };
  const removeQ = (i) => patchDraft({ questions: qs.filter((_, idx) => idx !== i) });
  const moveQ = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= qs.length) return;
    const next = [...qs];
    [next[i], next[j]] = [next[j], next[i]];
    patchDraft({ questions: next });
  };
  const addQ = () => patchDraft({ questions: [...qs, {
    key: `q_${Date.now()}`, text: "New question", kind: "yesno", required: false,
  }] });
  const duplicateQ = (i) => {
    const src = qs[i];
    const next = [...qs.slice(0, i + 1), { ...src, key: `${src.key}_copy_${Date.now()}` }, ...qs.slice(i + 1)];
    patchDraft({ questions: next });
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-4xl bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b hairline p-6 flex items-center justify-between z-10">
          <div>
            <p className="mono-label text-stone-400 text-[10px]">CONSENT FORM · EDITOR</p>
            <h3 className="font-serif text-2xl mt-1">{t.name}</h3>
            <p className="mono-label text-stone-500 text-[10px] mt-1">
              {t.key} · v{t.current_version || 0} · {t.status}{dirty && " · unsaved changes"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cf-preview" onClick={() => setPreviewing(true)} className="mono-label border hairline px-3 py-2 hover:bg-alt"><Eye size={12} className="inline" /> Preview</button>
            <button data-testid="cf-save-draft" onClick={saveDraft} className="mono-label border hairline px-3 py-2 hover:bg-alt">Save Draft</button>
            <button onClick={onClose} className="p-2 hover:bg-alt border hairline"><X size={16} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <label className="mono-label">Form Name (admin-facing)</label>
            <input value={t.name} onChange={(e) => { setT({ ...t, name: e.target.value }); setDirty(true); }}
              className="mt-1 w-full border hairline p-2 text-sm" data-testid="cf-name" />
          </section>
          <section>
            <label className="mono-label">Title (shown to client)</label>
            <input value={draft.title} onChange={(e) => patchDraft({ title: e.target.value })}
              className="mt-1 w-full border hairline p-2 text-sm" data-testid="cf-title" />
          </section>
          <section>
            <label className="mono-label">Disclosure / Intro Body</label>
            <textarea value={draft.intro} onChange={(e) => patchDraft({ intro: e.target.value })} rows={10}
              className="mt-1 w-full border hairline p-2 text-sm font-mono" data-testid="cf-intro" />
            <p className="text-[10px] text-stone-500 mt-1">Blank lines (double-newline) separate paragraphs. The client sees the first 4 lines with a "Read more" toggle.</p>
          </section>

          <section className="border-t hairline pt-6">
            <div className="flex items-center justify-between">
              <p className="mono-label">Questions ({qs.length})</p>
              <button data-testid="cf-add-q" onClick={addQ} className="mono-label border hairline px-3 py-1 hover:bg-alt"><Plus size={12} className="inline" /> Add</button>
            </div>
            <div className="mt-4 space-y-3">
              {qs.map((q, i) => (
                <div key={q.key + i} className="border hairline p-3" data-testid={`cf-q-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveQ(i, -1)} className="p-1 hover:bg-alt"><ChevronUp size={14} /></button>
                      <button onClick={() => moveQ(i, +1)} className="p-1 hover:bg-alt"><ChevronDown size={14} /></button>
                    </div>
                    <div className="flex-1 space-y-2">
                      <input value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })}
                        className="w-full border hairline p-2 text-sm" placeholder="Question text" />
                      <div className="flex flex-wrap gap-2 items-center">
                        <select value={q.kind || "yesno"} onChange={(e) => updateQ(i, { kind: e.target.value })}
                          className="border hairline p-1 text-xs">
                          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(i, { required: e.target.checked })} />Required</label>
                        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!q.require_yes} onChange={(e) => updateQ(i, { require_yes: e.target.checked })} />Must-be-Yes</label>
                        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!q.refuse_on_yes} onChange={(e) => updateQ(i, { refuse_on_yes: e.target.checked })} />Refuse-on-Yes</label>
                        <input value={q.key} onChange={(e) => updateQ(i, { key: e.target.value })}
                          className="border hairline p-1 text-xs w-40 font-mono" placeholder="key" />
                        <button onClick={() => duplicateQ(i)} className="p-1 hover:bg-alt" title="Duplicate"><Copy size={14} /></button>
                        <button onClick={() => removeQ(i)} className="p-1 hover:bg-alt text-red-600" title="Delete"><Trash2 size={14} /></button>
                      </div>
                      {(q.refuse_on_yes) && (
                        <input value={q.refuse_note || ""} onChange={(e) => updateQ(i, { refuse_note: e.target.value })}
                          className="w-full border hairline p-2 text-xs bg-amber-50" placeholder="Optional refuse-on-yes note shown to client" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="border-t hairline pt-6">
            <p className="mono-label">Publish</p>
            <textarea value={publishNote} onChange={(e) => setPublishNote(e.target.value)} rows={2}
              placeholder="Change note (optional) — logged in version history"
              className="mt-2 w-full border hairline p-2 text-xs" />
            <button data-testid="cf-publish" onClick={publish}
              className="mt-3 w-full mono-label bg-stone-900 text-white py-3 hover:bg-[#C5A059] transition" style={{ color: "#fff" }}>
              Publish to Client Booking Page
            </button>
            <p className="text-[10px] text-stone-500 mt-2">
              Publishing bumps the version and immediately updates the live client-facing form for future bookings. Historical signed consents are preserved unchanged.
            </p>
          </section>

          <section className="border-t hairline pt-6">
            <p className="mono-label">Version History</p>
            <ul className="mt-3 space-y-1 text-xs">
              {(t.versions || []).map((v) => (
                <li key={v.id} className="flex justify-between items-center py-1">
                  <span>v{v.version} · {(v.published_at || "").slice(0, 16).replace("T", " ")} · {v.published_by}</span>
                  <button onClick={() => restore(v.version)} className="mono-label border hairline px-2 py-1 hover:bg-alt" title="Restore as new draft"><RotateCcw size={10} className="inline mr-1" />Restore</button>
                </li>
              ))}
              {(t.versions || []).length === 0 && <li className="text-stone-500">No published versions yet.</li>}
            </ul>
          </section>
        </div>

        {previewing && (
          <div className="fixed inset-0 z-[60] bg-stone-900/70 flex items-center justify-center p-6" onClick={() => setPreviewing(false)}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <p className="mono-label">PREVIEW · {draft.title}</p>
                <button onClick={() => setPreviewing(false)} className="p-2 hover:bg-alt"><X size={16} /></button>
              </div>
              <div className="whitespace-pre-line text-sm text-stone-700 leading-relaxed border-b hairline pb-4">{draft.intro}</div>
              <div className="mt-6 space-y-3">
                {qs.map((q, i) => (
                  <div key={i} className="border-b hairline pb-3">
                    <p className="text-sm text-stone-900">{q.text} {q.required && <span className="text-red-600">*</span>}</p>
                    <p className="mono-label text-[10px] text-stone-400 mt-1">
                      kind: {q.kind}{q.require_yes ? " · must-be-yes" : ""}{q.refuse_on_yes ? " · refuse-on-yes" : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
